import { useMemo } from 'react';
import './QueueLegend.scss';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import { useQueueContext } from '../../../Queue/QueueContext';
import { useAuthContext } from 'context/useAuthContext';
import { HoneAbilityActions, HoneAbilitySubjects, QueueView } from '@hone-automation/common';
import CountBadge from '../../../KSQ2/Common/CountBadge/CountBadge';
import { useReportsStore } from 'hooks/useReportsStore';

const prefix = prefixClass('queue-legend');

/**
 * QueueLegend - Compact horizontal filter bar for the report search bar.
 * Shows Me, My Team, Client/KS, Completed views with count badges.
 */
export const QueueLegend = () => {
  const { state, dispatch, queryData } = useQueueContext();
  const { activeView } = state;
  const { viewCounts } = queryData;

  const { currentLocationAbility } = useAuthContext();
  const isAccountant = currentLocationAbility?.can(HoneAbilityActions.manage, HoneAbilitySubjects.Queue) ?? false;
  const setReportQueue = useReportsStore(s => s.setReportQueue);

  const items = useMemo<{ view: QueueView; label: string; color: string }[]>(
    () => [
      { view: 'Me', label: 'Me', color: 'var(--ks-queue-open)' },
      { view: 'My Team', label: 'My Team', color: 'var(--ks-queue-open)' },
      { view: 'Client', label: isAccountant ? 'Client' : 'KS', color: 'var(--ks-queue-in-progress)' },
      { view: 'Completed', label: 'Completed', color: 'var(--ks-queue-closed)' },
    ],
    [isAccountant]
  );

  const handleClick = (view: QueueView) => {
    setReportQueue(true);
    if (activeView === view) return;
    dispatch({ type: 'SET_ACTIVE_VIEW', payload: view });
  };

  return (
    <div className={prefix()}>
      {items.map(({ view, label, color }) => {
        const count = viewCounts[view] ?? 0;
        const isActive = activeView === view;

        return (
          <button
            key={view}
            type="button"
            className={classNames(prefix('item'), { active: isActive })}
            onClick={() => handleClick(view)}
          >
            <div className={prefix('color')} style={{ backgroundColor: color }} />
            <span className={prefix('label')}>{label}</span>
            <CountBadge count={count} size="small" className={prefix('badge')} />
          </button>
        );
      })}
    </div>
  );
};
